import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  SquaresFour, Users, Certificate, UploadSimple, Gavel,
  ClipboardText, Gear, SignOut, Heartbeat, BellRinging, ShieldCheck, UserCircle
} from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { Toaster } from "sonner";

const STAFF_NAV = [
  { to: "/", label: "Dashboard", icon: SquaresFour, end: true, testid: "nav-dashboard" },
  { to: "/practitioners", label: "Practitioners", icon: Users, testid: "nav-practitioners" },
  { to: "/credentials", label: "Credentials", icon: Certificate, testid: "nav-credentials" },
  { to: "/upload", label: "Upload & OCR", icon: UploadSimple, testid: "nav-upload" },
  { to: "/committee", label: "Committee", icon: Gavel, testid: "nav-committee" },
  { to: "/compliance", label: "Compliance", icon: ClipboardText, testid: "nav-compliance" },
  { to: "/reminders", label: "Reminders", icon: BellRinging, testid: "nav-reminders" },
  { to: "/hipaa", label: "HIPAA & Audit", icon: ShieldCheck, testid: "nav-hipaa" },
  { to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings" },
];

const PRACTITIONER_NAV = [
  { to: "/my-file", label: "My File", icon: UserCircle, end: true, testid: "nav-my-file" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const NAV = user?.role === "practitioner" ? PRACTITIONER_NAV : STAFF_NAV;

  return (
    <div className="min-h-screen flex bg-[#FAFBFC]" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="w-64 border-r border-gray-200 bg-white flex flex-col sticky top-0 h-screen">
        <div className="px-5 py-6 border-b border-gray-200 flex items-center gap-3">
          <div className="w-9 h-9 bg-slate-900 flex items-center justify-center">
            <Heartbeat size={18} weight="bold" className="text-white" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-gray-500">ASC</div>
            <div className="font-heading text-[15px] font-bold leading-tight text-slate-900">Credentialing</div>
          </div>
        </div>
        <nav className="flex-1 py-3">
          <div className="px-5 pb-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-gray-500">Workspace</div>
          {NAV.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-[13px] font-medium transition-colors border-l-2 ${
                  isActive
                    ? "bg-slate-50 text-slate-900 border-slate-900"
                    : "text-gray-600 hover:bg-gray-50 hover:text-slate-900 border-transparent"
                }`
              }
            >
              <Icon size={18} weight="regular" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-gray-200 p-4">
          <div className="text-[11px] text-gray-500 font-medium uppercase tracking-wider">Signed in as</div>
          <div className="text-sm font-semibold text-slate-900 truncate" data-testid="current-user-email">{user?.email}</div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="mt-3 w-full flex items-center justify-center gap-2 border border-gray-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-700 hover:bg-gray-100 hover:border-slate-900 transition-colors"
          >
            <SignOut size={14} weight="bold" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="p-6 md:p-8 max-w-[1600px] fade-up">
          <Outlet />
        </div>
      </main>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}
